'use client';

import { useMemo } from 'react';
import { ChevronLeftIcon, ChevronRightIcon, ChevronsLeftIcon, ChevronsRightIcon, EllipsisIcon } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@/components/ui/pagination';
import { cn } from '@/lib/utils';

interface Props {
  className?: string;
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}

export function PaginationWithEllipses({ className, currentPage, totalPages, onPageChange }: Props) {
  const pageNumbers = useMemo(() => {
    const maxPagesToShow = 5;
    let pageNumbers: (number | 'ellipsis')[] = [];

    if (totalPages <= maxPagesToShow) {
      // If we have fewer pages than our max, show all pages
      pageNumbers = Array.from({ length: totalPages }, (_, i) => i + 1);
    } else {
      // Always include first page
      pageNumbers.push(1);

      // Calculate start and end of page numbers to show
      let startPage = Math.max(2, currentPage - 1);
      let endPage = Math.min(totalPages - 1, currentPage + 1);

      // Adjust if we're near the beginning
      if (currentPage <= 3) {
        startPage = 2;
        endPage = 4;
      }

      // Adjust if we're near the end
      if (currentPage >= totalPages - 2) {
        startPage = totalPages - 3;
        endPage = totalPages - 1;
      }

      // Add ellipsis before middle pages if needed
      if (startPage > 2) {
        pageNumbers.push('ellipsis');
      }

      // Add middle pages
      for (let i = startPage; i <= endPage; i++) {
        pageNumbers.push(i);
      }

      // Add ellipsis after middle pages if needed
      if (endPage < totalPages - 1) {
        pageNumbers.push('ellipsis');
      }

      // Always include last page
      pageNumbers.push(totalPages);
    }

    return pageNumbers;
  }, [currentPage, totalPages]);

  if (totalPages <= 1) return null;

  return (
    <Pagination className={className}>
      <PaginationContent>
        <PaginationItem>
          <PaginationPrevious
            href='#'
            onClick={(e) => {
              e.preventDefault();
              if (currentPage > 1) onPageChange(currentPage - 1);
            }}
            className={currentPage === 1 ? 'pointer-events-none opacity-50' : ''}
          />
        </PaginationItem>

        {pageNumbers.map((pageNumber, index) => {
          if (pageNumber === 'ellipsis') {
            // People will not believe me, but using the PaginationEllipsis
            // breaks the overflow of the WHOLE page in a way that cannot be explained.
            // I ❤️ FE.
            return <EllipsisIcon className='size-4' key={`ellipsis-${index}`} />;
          }

          return (
            <PaginationItem key={pageNumber}>
              <PaginationLink
                href='#'
                onClick={(e) => {
                  e.preventDefault();
                  onPageChange(pageNumber);
                }}
                isActive={pageNumber === currentPage}
              >
                {pageNumber}
              </PaginationLink>
            </PaginationItem>
          );
        })}

        <PaginationItem>
          <PaginationNext
            href='#'
            onClick={(e) => {
              e.preventDefault();
              if (currentPage < totalPages) onPageChange(currentPage + 1);
            }}
            className={currentPage === totalPages ? 'pointer-events-none opacity-50' : ''}
          />
        </PaginationItem>
      </PaginationContent>
    </Pagination>
  );
}

export function PaginationWithIcons({ className, currentPage, totalPages, onPageChange }: Props) {
  const canGoBack = currentPage > 1;
  const canGoForward = currentPage < totalPages;

  return (
    <nav className={cn('flex items-center gap-4', className)}>
      <div className='text-sm font-medium'>
        Page {currentPage.toLocaleString()} of {totalPages.toLocaleString()}
      </div>

      <div className='flex items-center gap-1'>
        <Button variant='outline' size='icon' className='size-8' disabled={!canGoBack} onClick={() => onPageChange(1)}>
          <ChevronsLeftIcon />
        </Button>
        <Button
          variant='outline'
          size='icon'
          className='size-8'
          disabled={!canGoBack}
          onClick={() => onPageChange(currentPage - 1)}
        >
          <ChevronLeftIcon />
        </Button>
        <Button
          variant='outline'
          size='icon'
          className='size-8'
          disabled={!canGoForward}
          onClick={() => onPageChange(currentPage + 1)}
        >
          <ChevronRightIcon />
        </Button>
        <Button
          variant='outline'
          size='icon'
          className='size-8'
          disabled={!canGoForward}
          onClick={() => onPageChange(totalPages)}
        >
          <ChevronsRightIcon />
        </Button>
      </div>
    </nav>
  );
}
